import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

// The page-wide Escape stack (accessibility: "Exactly one layer responds to
// Escape: the topmost. Overlays register on a stack and the top one consumes
// the event"). Same idea as Radix's dismissable-layer stack.
// Deviation from Base UI defaults: Escape closes only the topmost layer
// page-wide, not only within one React tree; tested.

type Layer = { onEscape?: () => void; blocking: boolean }

const layers: Layer[] = []

function isEscape(event: KeyboardEvent) {
  return event.key === 'Escape' && !event.defaultPrevented && !event.isComposing
}

function onDocumentKeyDown(event: KeyboardEvent) {
  if (!isEscape(event)) return
  const top = layers.at(-1)
  // A top layer without onEscape is a Base UI overlay, which handles its own.
  if (!top?.onEscape) return
  event.preventDefault()
  top.onEscape()
}

// A non-blocking layer (a tooltip) still takes Escape while it shows, but does
// not hold off app shortcuts such as ⌘K (`hasOpenLayer`).
type LayerOptions = { blocking?: boolean }

export function useEscapeLayer(
  open: boolean,
  onEscape?: () => void,
  { blocking = true }: LayerOptions = {},
): () => boolean {
  const onEscapeRef = useRef(onEscape)
  useLayoutEffect(() => {
    onEscapeRef.current = onEscape
  })
  const [layer] = useState<Layer>(() => ({
    blocking,
    get onEscape() {
      return onEscapeRef.current
    },
  }))

  useLayoutEffect(() => {
    if (!open) return
    layers.push(layer)
    if (layers.length === 1) {
      document.addEventListener('keydown', onDocumentKeyDown)
    }
    return () => {
      const index = layers.indexOf(layer)
      if (index !== -1) layers.splice(index, 1)
      if (layers.length === 0) {
        document.removeEventListener('keydown', onDocumentKeyDown)
      }
    }
  }, [open, layer])

  return useCallback(() => layers.at(-1) === layer, [layer])
}

type OpenChangeDetails = {
  reason: string
  cancel(): void
  allowPropagation(): void
}

export function useOverlayLayer<D extends OpenChangeDetails>(
  open: boolean,
  onOpenChange: (next: boolean, details: D) => void,
  options?: LayerOptions,
): (next: boolean, details: D) => void {
  const isTop = useEscapeLayer(open, undefined, options)
  return (next, details) => {
    if (!next && details.reason === 'escape-key' && !isTop()) {
      // A lower layer neither closes nor swallows the key, so the top layer
      // still gets it.
      details.cancel()
      details.allowPropagation()
      return
    }
    onOpenChange(next, details)
  }
}

/** Whether a blocking layer is open; app shortcuts other than Escape check it. */
export function hasOpenLayer(): boolean {
  return layers.some((layer) => layer.blocking)
}

export function useEscapeShortcut(handler: () => void, enabled = true): void {
  const handlerRef = useRef(handler)
  useLayoutEffect(() => {
    handlerRef.current = handler
  })

  useEffect(() => {
    if (!enabled) return
    function onKeyDown(event: KeyboardEvent) {
      // Any layer, a tooltip included, takes Escape before the shortcut.
      if (!isEscape(event) || layers.length > 0) return
      handlerRef.current()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [enabled])
}
